import { createContext, useContext, useMemo, useState } from 'react';
import useGlobalFilterState from 'components/molecules/common/NewGlobalFilter/hooks/useGlobalFilterState';
import type { GlobalFilterState } from 'components/molecules/common/NewGlobalFilter/hooks/useGlobalFilterState';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import type { PlanDetails } from '../CalendarPage/hooks/queries/useCalendarData';

interface PlanDetailsContextValue {
    activeView: 'calendar' | 'metric-view' | 'event';
    setActiveView: (view: 'calendar' | 'metric-view' | 'event') => void;
    currentPlanDetails: PlanDetails | null;
    setCurrentPlanDetails: (planDetails: PlanDetails | null) => void;
    editEventId: string | null;
    setEditEventId: (eventId: string | null) => void;
    editPromoId: string | null;
    setEditPromoId: (promoId: string | null) => void;
    autoAddPromo: boolean;
    setAutoAddPromo: (value: boolean) => void;
    hasUnsavedNoPromoEvent: boolean;
    setHasUnsavedNoPromoEvent: (value: boolean) => void;
    filterState: GlobalFilterState;
}

const noopFilterState: GlobalFilterState = {
    availableFilters: [],
    allCheckedAttribute: {},
    allSelectedValueForAllAttribute: {},
    applyFilterObject: {},
    selectedFilter: null,
    setSelectedFilter: () => {},
    tempApplyFilterObject: {},
    setTempApplyFilterObject: () => {},
    filters: {},
    filterApiVariables: undefined,
    resetFilterObject: () => {},
    onFilterSaveClickHandler: () => {},
};

const PlanDetailsContext = createContext<PlanDetailsContextValue>({
    activeView: 'calendar',
    setActiveView: () => {},
    currentPlanDetails: null,
    setCurrentPlanDetails: () => {},
    editEventId: null,
    setEditEventId: () => {},
    editPromoId: null,
    setEditPromoId: () => {},
    autoAddPromo: false,
    setAutoAddPromo: () => {},
    hasUnsavedNoPromoEvent: false,
    setHasUnsavedNoPromoEvent: () => {},
    filterState: noopFilterState,
});

export const usePlanDetailsContext = () => {
    return useContext(PlanDetailsContext);
};

export const PlanDetailsProvider = ({ children }: { children: React.ReactNode }) => {
    const [activeView, setActiveView] = useState<'calendar' | 'metric-view' | 'event'>('calendar');
    const [currentPlanDetails, setCurrentPlanDetails] = useState<PlanDetails | null>(null);
    const [editEventId, setEditEventId] = useState<string | null>(null);
    const [editPromoId, setEditPromoId] = useState<string | null>(null);
    const [autoAddPromo, setAutoAddPromo] = useState(false);
    const [hasUnsavedNoPromoEvent, setHasUnsavedNoPromoEvent] = useState(false);

    const tpoGenConfig = useTpoGenConfig();
    const filterState = useGlobalFilterState(tpoGenConfig.calendarFilterKeys);

    const value = useMemo<PlanDetailsContextValue>(
        () => ({
            activeView,
            setActiveView,
            currentPlanDetails,
            setCurrentPlanDetails,
            editEventId,
            setEditEventId,
            editPromoId,
            setEditPromoId,
            autoAddPromo,
            setAutoAddPromo,
            hasUnsavedNoPromoEvent,
            setHasUnsavedNoPromoEvent,
            filterState,
        }),
        [
            activeView,
            currentPlanDetails,
            editEventId,
            editPromoId,
            autoAddPromo,
            hasUnsavedNoPromoEvent,
            filterState,
        ],
    );

    return <PlanDetailsContext.Provider value={value}>{children}</PlanDetailsContext.Provider>;
};
